import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classnames from 'classnames';
import React, { createContext, useState, useCallback, useMemo } from 'react';
import TableBody from './TableBody.js';
import TableData from './TableData.js';
import TableHead from './TableHead.js';
import TableHeader from './TableHeader.js';
import TableRow from './TableRow.js';
import { partition, getEpoch } from './utility.js';

var _excluded = ["alternateRowColor", "cellPadding", "className", "children", "height", "name", "onSort", "width"];
var TableContext = /*#__PURE__*/createContext();
TableContext.displayName = 'Table.Context';
var Table = function Table(_ref) {
  var alternateRowColor = _ref.alternateRowColor,
    _ref$cellPadding = _ref.cellPadding,
    cellPadding = _ref$cellPadding === void 0 ? 'normal' : _ref$cellPadding,
    className = _ref.className,
    propsChildren = _ref.children,
    height = _ref.height,
    name = _ref.name,
    onSort = _ref.onSort,
    width = _ref.width,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  // Our collection of manipulated children
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2);
    _useState2[0];
    _useState2[1];
  var _useState3 = useState('none'),
    _useState4 = _slicedToArray(_useState3, 2),
    sortOrder = _useState4[0],
    setSortOrder = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    sortIndex = _useState6[0],
    setSortIndex = _useState6[1];
  var _useState7 = useState('character'),
    _useState8 = _slicedToArray(_useState7, 2),
    sortType = _useState8[0],
    setSortType = _useState8[1]; // boolean, character, date, numeric

  var sortBodyRows = useCallback(function (bodyChildren) {
    // We can't guarantee only "Table.Row" children exist so...
    // bucket all the Table.Row children into on array, and the remainder the another
    var _partition = partition(React.Children.toArray(bodyChildren.props.children), function (child) {
        var _child$type;
        return (child === null || child === void 0 ? void 0 : (_child$type = child.type) === null || _child$type === void 0 ? void 0 : _child$type.displayName) === 'Table.Row';
      }),
      _partition2 = _slicedToArray(_partition, 2),
      rowChildren = _partition2[0],
      otherChildren = _partition2[1];
    if (sortIndex !== null) {
      // sort rowChildren in place
      rowChildren.sort(function (a, b) {
        // use sortValue prop if set
        var valueOfA = a.props.children[sortIndex].props // does the child have a "props" property?
        ? a.props.children[sortIndex].props.sortValue || a.props.children[sortIndex].props.children || false : false; // If a "props" property does not exist, default to false

        var valueOfB = b.props.children[sortIndex].props ? b.props.children[sortIndex].props.sortValue || b.props.children[sortIndex].props.children || false : false;

        // Boolean sort column values
        if (sortType === 'boolean') {
          // Values of children will always evaluate to true, only use sortValue

          var boolSortValA = a.props.children[sortIndex].props && a.props.children[sortIndex].props.sortValue ? Boolean(a.props.children[sortIndex].props.sortValue) : -1;
          var boolSortValB = b.props.children[sortIndex].props && b.props.children[sortIndex].props.sortValue ? Boolean(b.props.children[sortIndex].props.sortValue) : -1;
          if (sortOrder === 'dsc') {
            return boolSortValA === boolSortValB ? 0 : boolSortValA ? -1 : 1;
          }
          if (sortOrder === 'asc') {
            return boolSortValA === boolSortValB ? 0 : boolSortValA ? 1 : -1;
          }
        }
        // Alphabetically sort column values
        if (sortType === 'character') {
          // needs to be converted to always uppercase in order to correctly sort caps & non-caps values together
          valueOfA = valueOfA ? valueOfA.toUpperCase() : '';
          valueOfB = valueOfB ? valueOfB.toUpperCase() : '';
          if (sortOrder === 'dsc') {
            return valueOfA > valueOfB ? -1 : valueOfA > valueOfB ? 1 : 0;
          }
          if (sortOrder === 'asc') {
            return valueOfA < valueOfB ? -1 : valueOfA > valueOfB ? 1 : 0;
          }
        }
        // Numerically sort column values
        if (sortType === 'numeric') {
          // if this comes in as a string, we remove comas and
          // change all values to numbers for the most accurate sorting
          if (typeof valueOfA === 'string') {
            valueOfA = parseFloat(valueOfA.split(',').join(''));
          }
          if (typeof valueOfB === 'string') {
            valueOfB = parseFloat(valueOfB.split(',').join(''));
          }
          if (sortOrder === 'dsc') {
            return valueOfB - valueOfA;
          }
          if (sortOrder === 'asc') {
            return valueOfA - valueOfB;
          }
        }
        // Sort column values by Date
        if (sortType === 'date') {
          if (sortOrder === 'dsc') {
            return getEpoch(valueOfB) - getEpoch(valueOfA);
          }
          if (sortOrder === 'asc') {
            return getEpoch(valueOfA) - getEpoch(valueOfB);
          }
        }
      });
    }
    // merge children nodes back into one collection within the body component
    return /*#__PURE__*/React.createElement(Table.Body, bodyChildren.props, [].concat(_toConsumableArray(otherChildren), _toConsumableArray(rowChildren)));
  }, [sortIndex, sortOrder, sortType]);

  // Iterates over the Table's children and sorts rows within Table.Body
  // Memorize the returned value so we don't call this on every render
  var children = useMemo(function () {
    var childNodes = React.Children.map(propsChildren, function (child) {
      var _child$type2;
      if ((child === null || child === void 0 ? void 0 : (_child$type2 = child.type) === null || _child$type2 === void 0 ? void 0 : _child$type2.displayName) === 'Table.Body') {
        return sortBodyRows(child);
      }
      return child;
    });
    return childNodes;
  }, [propsChildren, sortBodyRows]);
  var finalClasses = classnames('C-Table', _defineProperty({
    '--alternateRowColor': alternateRowColor
  }, "--cell-padding-".concat(cellPadding), cellPadding), className);
  var finalScrollContainerClasses = classnames('C-TableScrollContainer', {
    '--overflow-x': width,
    '--overflow-y': height
  }, className);

  // each time sort is triggered, set the active column, redetermine the sort order and fire the onSort function
  var handleOnSort = function handleOnSort(event, sort, columnIndex, sortType) {
    setSortIndex(columnIndex);
    setSortOrder(sort);
    setSortType(sortType);
    onSort && onSort(event, sort, columnIndex, sortType);
  };

  // Values for Table Context
  var parentProps = {
    onSort: function onSort(event, sort, columnIndex, sortType) {
      handleOnSort(event, sort, columnIndex, sortType);
    },
    sortColumnIndex: sortIndex
  };
  if (width || height) {
    return /*#__PURE__*/React.createElement(TableContext.Provider, {
      value: parentProps
    }, /*#__PURE__*/React.createElement("div", {
      tabIndex: "0",
      className: finalScrollContainerClasses,
      style: {
        maxHeight: height
      }
    }, /*#__PURE__*/React.createElement("table", _extends({
      className: finalClasses,
      style: {
        minWidth: width
      },
      "aria-label": name
    }, otherProps), children)));
  } else {
    return /*#__PURE__*/React.createElement(TableContext.Provider, {
      value: parentProps
    }, /*#__PURE__*/React.createElement("table", _extends({
      className: finalClasses,
      "aria-label": name
    }, otherProps), children));
  }
};
Table.displayName = 'Table';
Table.Body = TableBody;
Table.Data = TableData;
Table.Head = TableHead;
Table.Header = TableHeader;
Table.Row = TableRow;

export { Table, TableContext, Table as default };
